import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Disclaimer, Spinner, PriorityBadge, EmptyState } from "@/components/shared";
import { RefreshCw, ChevronDown, Lock, Plus, ExternalLink, ClipboardList, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";

function ConfidencePrompt({ roadmapId, onDone }) {
  const [choice, setChoice] = useState(null);
  const [comment, setComment] = useState("");
  const submit = async (response) => {
    setChoice(response);
    if (response !== "not_yet") {
      await api.post("/feedback", { response, roadmap_id: roadmapId });
      toast.success("Thank you for sharing.");
      onDone();
    }
  };
  const submitComment = async () => {
    await api.post("/feedback", { response: "not_yet", comment, roadmap_id: roadmapId });
    toast.success("Thank you — this helps us improve.");
    onDone();
  };
  return (
    <div className="kid-card p-6 mb-6 bg-kid-skytint/50" data-testid="confidence-prompt">
      <h3 className="font-bold text-kid-navy text-lg mb-3">Do you feel clearer about your next steps?</h3>
      {choice !== "not_yet" ? (
        <div className="flex flex-wrap gap-2">
          {[["yes", "Yes"], ["somewhat", "Somewhat"], ["not_yet", "Not yet"]].map(([v, l]) => (
            <button key={v} onClick={() => submit(v)} data-testid={`confidence-${v}`} className="kid-btn kid-btn-ghost h-10 px-5">{l}</button>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-kid-muted">What still feels unclear?</p>
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
            className="w-full rounded-xl border border-kid-line p-3 text-sm" data-testid="confidence-comment" placeholder="Tell us what would help…" />
          <button onClick={submitComment} className="kid-btn kid-btn-primary h-10 px-5" data-testid="confidence-submit">Submit</button>
        </div>
      )}
    </div>
  );
}

function Section({ section, roadmapId, isPaid, onItemStatus, focused }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`kid-card overflow-hidden ${focused ? "ring-2 ring-kid-teal/60" : ""}`} data-testid={`roadmap-section-${section.section_key}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 text-left">
        <span className="font-display font-bold text-kid-navy text-lg">{section.title}</span>
        <ChevronDown className={`text-kid-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-5 space-y-3">
          {section.items.map((item) => (
            <div key={item.item_id} className="border border-kid-line rounded-2xl p-4" data-testid="roadmap-item">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <PriorityBadge priority={item.priority} />
                    {item.status === "saved" && <span className="text-[.65rem] text-kid-green font-poppins font-semibold uppercase">Saved</span>}
                  </div>
                  <h4 className="font-semibold text-kid-navy">{item.title}</h4>
                  {item.locked ? (
                    <p className="text-sm text-kid-muted mt-1 flex items-center gap-1.5"><Lock size={13} /> Upgrade to Family Path to see this recommendation.</p>
                  ) : (
                    <p className="text-sm text-kid-muted mt-1">{item.description}</p>
                  )}
                </div>
              </div>
              {!item.locked && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {item.program_id && (
                    <Link to={`/app/funding/${item.program_id}`} className="text-sm text-kid-teal font-poppins font-semibold inline-flex items-center gap-1" data-testid="roadmap-item-program">
                      View program <ExternalLink size={13} />
                    </Link>
                  )}
                  {isPaid && (
                    <button onClick={() => onItemStatus(item)} className="text-sm text-kid-teal font-poppins font-semibold inline-flex items-center gap-1" data-testid="roadmap-item-add-task">
                      <Plus size={14} /> Add to tasks
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Roadmap() {
  const { isPaid, status } = useAuth();
  const [params] = useSearchParams();
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showConfidence, setShowConfidence] = useState(params.get("new") === "1");

  const load = async () => {
    const { data } = await api.get("/roadmaps/current");
    setRoadmap(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const focus = params.get("focus");
  useEffect(() => {
    if (!loading && roadmap && focus) {
      const el = document.querySelector(`[data-testid="roadmap-section-${focus}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, roadmap, focus]);

  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/roadmaps/refresh"); setRoadmap(data); toast.success("Roadmap refreshed."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addTask = async (item) => {
    try {
      await api.post("/tasks/from-roadmap-item", { roadmap_id: roadmap.id, item_id: item.item_id });
      await api.patch(`/roadmaps/${roadmap.id}/items/${item.item_id}`, { status: "saved" });
      toast.success("Added to your tasks.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportBinder = async () => {
    try {
      const { data } = await api.post("/binder/token");
      window.open(`${API}/binder/pdf?token=${encodeURIComponent(data.token)}`, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not export your binder."); }
  };

  if (loading) return <Spinner label="Loading your roadmap…" />;

  if (!roadmap) {
    return (
      <div>
        <PageHeader eyebrow="Your roadmap" title="Family Support Roadmap" />
        <EmptyState icon={ClipboardList} testid="roadmap-empty" title="No roadmap yet"
          body="Complete the guided intake to generate your personalized roadmap."
          action={<Link to="/app/onboarding" className="kid-btn kid-btn-primary h-12 px-7">{status.intake_status === "in_progress" ? "Resume intake" : "Start intake"}</Link>} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Your roadmap" title="Family Support Roadmap"
        subtitle="Prioritized next steps, organized just for your family."
        action={<div className="flex gap-2">
          <button onClick={exportBinder} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="roadmap-export-pdf">
            <Download size={16} /> Export Family Binder</button>
          <button onClick={refresh} disabled={busy} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="roadmap-refresh">
            <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> Refresh</button>
        </div>} />

      {roadmap.status === "stale" && (
        <div className="text-sm text-kid-navy bg-kid-sun/10 border border-kid-sun/30 rounded-xl px-4 py-3 mb-5" data-testid="roadmap-stale">
          Your family information has changed. Refresh your roadmap to update your recommended next steps.
        </div>
      )}

      {showConfidence && <ConfidencePrompt roadmapId={roadmap.id} onDone={() => setShowConfidence(false)} />}

      {roadmap.is_limited && (
        <div className="rounded-2xl p-5 mb-5 text-white flex items-center justify-between gap-4" style={{ background: "linear-gradient(135deg,#159389,#1FA5A0)" }} data-testid="roadmap-limited-banner">
          <div><div className="font-display font-extrabold text-lg">You're seeing your starter roadmap</div>
            <p className="text-white/85 text-sm">Upgrade to Family Path to unlock every recommendation and full guidance.</p></div>
          <Link to="/app/account" className="kid-btn bg-white text-kid-tealcta h-11 px-5 shrink-0"><Sparkles size={16} /> Upgrade</Link>
        </div>
      )}

      <div className="space-y-4 kid-stagger">
        {roadmap.sections.map((s) => (
          <Section key={s.section_key} section={s} roadmapId={roadmap.id} isPaid={isPaid} onItemStatus={addTask} focused={focus === s.section_key} />
        ))}
      </div>

      <div className="mt-6">
        <Disclaimer testid="roadmap-disclaimer">{roadmap.disclaimer}</Disclaimer>
      </div>
    </div>
  );
}
